import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2, Sparkles } from "lucide-react";
import { SectionTitle } from "./ui";
import ProductCard from "./ProductCard";
import api from "../../lib/api";

// --- shared cached fetch for homepage CMS content --------------------------
let _hpPromise = null;
function useHomepage() {
  const [hp, setHp] = useState(null);
  useEffect(() => {
    if (!_hpPromise) _hpPromise = api.get("/content/homepage").then((r) => r.data).catch(() => ({}));
    _hpPromise.then(setHp);
  }, []);
  return hp;
}

const Overlay = () => <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/55 to-ink/20" />;

// --- 4. Selling season full-width banner -----------------------------------
function seasonWindow(s) {
  const start = s.sales_start ? new Date(`${s.sales_start.slice(0, 10)}T00:00:00`) : null;
  const end = s.sales_end ? new Date(`${s.sales_end.slice(0, 10)}T23:59:59`) : null;
  return { start, end };
}

function pickActiveSeason(seasons) {
  const now = Date.now();
  const enabled = (seasons || []).filter((s) => s.enabled !== false && (s.image || s.heading));
  // 1) currently within the sales window
  const current = enabled.find((s) => {
    const { start, end } = seasonWindow(s);
    return (!start || now >= start.getTime()) && (!end || now <= end.getTime());
  });
  if (current) return { season: current, mode: "current" };
  // 2) soonest upcoming season
  const upcoming = enabled
    .filter((s) => { const { start } = seasonWindow(s); return start && now < start.getTime(); })
    .sort((a, b) => seasonWindow(a).start - seasonWindow(b).start)[0];
  if (upcoming) return { season: upcoming, mode: "upcoming" };
  return { season: null, mode: null };
}

function useCountdown(targetMs) {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!targetMs) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [targetMs]);
  if (!targetMs) return null;
  const diff = targetMs - Date.now();
  if (diff <= 0) return null;
  return {
    d: Math.floor(diff / 86400000),
    h: Math.floor((diff / 3600000) % 24),
    m: Math.floor((diff / 60000) % 60),
    s: Math.floor((diff / 1000) % 60),
  };
}

function SeasonCountdown({ label, targetMs }) {
  const t = useCountdown(targetMs);
  if (!t) return null;
  return (
    <div className="mt-5" data-testid="season-countdown">
      <p className="text-[11px] uppercase tracking-[0.2em] text-gold/90 mb-2">{label}</p>
      <div className="flex items-center gap-2 sm:gap-3">
        {[["Days", t.d], ["Hrs", t.h], ["Min", t.m], ["Sec", t.s]].map(([l, v]) => (
          <div key={l} className="text-center">
            <div className="grid place-items-center h-12 w-12 sm:h-14 sm:w-14 rounded-xl bg-black/50 border border-gold/25 font-cinzel text-lg sm:text-xl text-gold tabular-nums">{String(v).padStart(2, "0")}</div>
            <span className="mt-1 block text-[9px] uppercase tracking-widest text-white/50">{l}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function SeasonalBanner() {
  const hp = useHomepage();
  if (!hp) return null;
  // New Selling Seasons model, with fallback to the legacy single seasonal_banner.
  const hasSeasons = Array.isArray(hp.seasons) && hp.seasons.length > 0;
  const { season, mode } = hasSeasons ? pickActiveSeason(hp.seasons) : { season: null, mode: null };
  const legacy = hp.seasonal_banner;
  const b = season || (!hasSeasons && legacy && legacy.enabled !== false && legacy.image ? legacy : null);
  if (!b || !b.image) return null;

  const { start, end } = seasonWindow(b);
  const cd = mode === "upcoming" && start
    ? { label: "Sale starts in", target: start.getTime() }
    : end ? { label: "Sale ends in", target: end.getTime() } : null;

  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10" data-testid="seasonal-banner">
      <div className="group relative overflow-hidden rounded-3xl border border-white/10 min-h-[340px] sm:min-h-[420px]">
        <img src={b.image} alt={b.heading} className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/70 to-transparent" />
        <div className="relative h-full flex flex-col justify-center max-w-lg px-8 sm:px-12 py-10">
          {b.eyebrow && <p className="font-cinzel uppercase tracking-[0.28em] text-xs text-gold mb-3">{b.eyebrow}</p>}
          <h2 className="font-playfair text-3xl sm:text-5xl text-white leading-tight">{b.heading}</h2>
          {b.description && <p className="mt-3 text-white/75 text-base sm:text-lg">{b.description}</p>}
          {cd && <SeasonCountdown label={cd.label} targetMs={cd.target} />}
          {b.cta_label && (
            <Link to={b.cta_url || "/fireworks"} data-testid="seasonal-banner-cta" className="mt-6 inline-flex w-fit items-center gap-2 cf-gold-btn rounded-lg font-semibold h-12 px-7">
              {b.cta_label} <ArrowRight className="h-4 w-4" />
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}

// --- 6. Editorial large category grid --------------------------------------
export function HomeCollections() {
  const [cats, setCats] = useState([]);
  useEffect(() => { api.get("/categories").then(({ data }) => setCats(data)).catch(() => {}); }, []);
  const items = cats
    .filter((c) => c.image && !/^TEST_/i.test(c.name || ""))
    .sort((a, b) => (a.sort_order ?? 99) - (b.sort_order ?? 99))
    .filter((c, i, arr) => {
      const key = (c.name || "").toLowerCase().trim().split(/[\s&]+/)[0];
      return arr.findIndex((x) => (x.name || "").toLowerCase().trim().split(/[\s&]+/)[0] === key) === i;
    })
    .slice(0, 8);
  if (!items.length) return null;
  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14" data-testid="home-collections">
      <SectionTitle eyebrow="The Range" title="Shop by Category" subtitle="Browse our curated ranges — big, bold and built for the perfect display." />
      <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-[170px] sm:auto-rows-[200px]">
        {items.map((c, i) => {
          const large = i === 0;
          return (
            <Link key={c.category_id} to={`/fireworks/${c.slug}`} data-testid={`home-category-${c.slug}`}
              className={`group relative overflow-hidden rounded-2xl border border-white/10 hover:border-gold/60 transition-all block ${large ? "col-span-2 row-span-2" : ""}`}>
              <img src={c.image} alt={c.name} loading={large ? "eager" : "lazy"} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <Overlay />
              <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5">
                <p className={`font-playfair text-white drop-shadow ${large ? "text-2xl sm:text-4xl" : "text-lg sm:text-xl"}`}>{c.name}</p>
                {large && c.short_description && <p className="text-white/70 text-sm mt-1 line-clamp-2 max-w-sm">{c.short_description}</p>}
                <span className="mt-1 inline-flex items-center gap-1 text-xs sm:text-sm text-gold font-semibold">View Category <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" /></span>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

// --- 5. Featured offers — driven by the live Promotions API ----------------
function overlayStyle(strength, align) {
  const a = Math.min(100, Math.max(0, strength ?? 60)) / 100;
  const dir = align === "right" ? "to left" : align === "center" ? "to top" : "to right";
  return { background: `linear-gradient(${dir}, rgba(5,7,13,${(a + 0.25).toFixed(2)}) 0%, rgba(5,7,13,${(a * 0.75).toFixed(2)}) 55%, rgba(5,7,13,${(a * 0.25).toFixed(2)}) 100%)` };
}

function PromoCard({ p }) {
  const align = p.text_align || "left";
  const alignCls = align === "center" ? "items-center text-center mx-auto" : align === "right" ? "items-end text-right ml-auto" : "items-start text-left";
  const objPos = p.image_focal_point === "top" ? "object-top" : p.image_focal_point === "bottom" ? "object-bottom" : "object-center";
  return (
    <div data-testid={`promo-card-${p.promotion_id}`} className={`group relative overflow-hidden rounded-2xl border border-white/10 min-h-[220px] sm:min-h-[240px] ${p.is_featured ? "md:col-span-2 md:min-h-[300px]" : ""}`}>
      {p.desktop_image ? (
        <picture>
          {p.mobile_image && <source media="(max-width: 640px)" srcSet={p.mobile_image} />}
          <img src={p.desktop_image} alt={p.name} loading="lazy" className={`absolute inset-0 h-full w-full object-cover ${objPos} transition-transform duration-[1200ms] group-hover:scale-105`} />
        </picture>
      ) : (
        <div className="absolute inset-0" style={{ background: "radial-gradient(120% 120% at 25% 20%, #1a2140 0%, #0B1020 55%, #05070D 100%)" }} />
      )}
      <div className="absolute inset-0" style={overlayStyle(p.overlay_strength, align)} />
      <div className={`relative h-full flex flex-col justify-center gap-2 px-7 py-8 max-w-md ${alignCls}`}>
        {p.badge_label && <span className="inline-flex w-fit items-center gap-1.5 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-gold"><Sparkles className="h-3 w-3" /> {p.badge_label}</span>}
        <h3 className={`font-playfair text-white ${p.is_featured ? "text-3xl sm:text-4xl" : "text-2xl"}`}>{p.name}</h3>
        {p.short_description && <p className="text-white/75 text-sm">{p.short_description}</p>}
        {p.terms_summary && <p className="text-white/40 text-[11px]">{p.terms_summary}</p>}
        <Link to={p.cta_url || "/fireworks"} data-testid={`promo-cta-${p.promotion_id}`} className="mt-2 inline-flex w-fit items-center gap-2 cf-gold-btn rounded-lg font-semibold h-11 px-6">{p.cta_label || "Shop Offer"} <ArrowRight className="h-4 w-4" /></Link>
      </div>
    </div>
  );
}

export function FeaturedOffers() {
  const [promos, setPromos] = useState(null);
  useEffect(() => { api.get("/promotions/homepage").then(({ data }) => setPromos(data)).catch(() => setPromos([])); }, []);
  if (!promos || !promos.length) return null; // empty state: hide section entirely
  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10" data-testid="featured-offers">
      <SectionTitle eyebrow="Deals" title="Featured Offers" subtitle="Premium fireworks, exceptional value." />
      <div className="mt-8 grid gap-5 md:grid-cols-2">
        {promos.map((p) => <PromoCard key={p.promotion_id} p={p} />)}
      </div>
    </section>
  );
}

// --- 8. Popular products rails ---------------------------------------------
function Rail({ eyebrow, title, products, viewAllTo }) {
  if (!products.length) return null;
  const id = title.toLowerCase().replace(/\s+/g, "-");
  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10" data-testid={`home-rail-${id}`}>
      <div className="flex items-end justify-between gap-4">
        <SectionTitle eyebrow={eyebrow} title={title} />
        <Link to={viewAllTo} className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-gold hover:text-amber whitespace-nowrap pb-2" data-testid={`view-all-${id}`}>View all <ArrowRight className="h-4 w-4" /></Link>
      </div>
      <div className="mt-8 grid gap-5 grid-cols-2 lg:grid-cols-4">
        {products.slice(0, 4).map((p, i) => <ProductCard key={p.product_id} product={p} index={i} />)}
      </div>
    </section>
  );
}

export function PopularProducts() {
  const [featured, setFeatured] = useState([]);
  const [best, setBest] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const imaged = (x) => x.filter((p) => (p.images || []).length > 0).slice(0, 4);
    Promise.all([
      api.get("/products", { params: { sort: "featured", limit: 16 } }).then(({ data }) => setFeatured(imaged(data.items))).catch(() => {}),
      api.get("/products", { params: { sort: "best-sellers", limit: 16 } }).then(({ data }) => setBest(imaged(data.items))).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-gold" /></div>;
  return (
    <>
      <Rail eyebrow="Popular" title="Popular Fireworks" products={featured} viewAllTo="/fireworks?sort=featured" />
      <Rail eyebrow="Crowd Favourites" title="Best Sellers" products={best} viewAllTo="/fireworks?sort=best-sellers" />
    </>
  );
}

// --- 9. Current Offers — auto-generated from the Promotion Engine ----------
export function CurrentOffers() {
  const hp = useHomepage();
  const [offers, setOffers] = useState(null);
  const limit = hp?.current_offers_count || 6;
  useEffect(() => {
    api.get("/promotions/current", { params: { limit } }).then(({ data }) => setOffers(data)).catch(() => setOffers([]));
  }, [limit]);
  if (hp?.show_current_offers === false) return null;
  if (!offers || !offers.length) return null;
  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14" data-testid="home-current-offers">
      <div className="flex items-end justify-between gap-4">
        <SectionTitle eyebrow="Save more" title="Current Offers" subtitle="Live promotions, applied automatically at checkout." />
        <Link to="/offers" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-gold hover:text-amber whitespace-nowrap pb-2" data-testid="view-all-offers">All offers <ArrowRight className="h-4 w-4" /></Link>
      </div>
      <div className="mt-8 space-y-10">
        {offers.map((o) => (
          <div key={o.promotion_id} data-testid={`current-offer-${o.slug}`}>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-gold text-ink px-3.5 py-1.5 text-sm font-bold uppercase tracking-wide" data-testid={`current-offer-type-${o.slug}`}><Sparkles className="h-4 w-4" /> {o.ribbon}</span>
              {o.ends_label && <span className="text-xs text-white/50">Ends {o.ends_label}</span>}
              <Link to="/offers" className="text-xs text-gold hover:underline ml-auto">View all eligible products →</Link>
            </div>
            <div className="mt-5 grid gap-5 grid-cols-2 lg:grid-cols-4">
              {(o.products || []).slice(0, 4).map((p, i) => <ProductCard key={p.product_id} product={p} index={i} />)}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

